import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useSearchStore } from "../../../hooks/useSearchStore";
import styles from "./GuestPicker.module.css";

export const GuestPicker = () => {
  const {
    rooms,
    roomsText,
    adults,
    children,
    guests,
    isDefault,
    setRooms,
    setAdults,
    setChildren,
    reset,
  } = useSearchStore();

  const data = useMemo(
    () => [
      {
        key: "rooms",
        title: "Rooms",
        value: roomsText,
        disabledMin: rooms <= 1,
        disabledMax: rooms >= 6,
      },
      {
        key: "adults",
        title: "Adults",
        subtitle: "(Max: 8 total guests/room)",
        value: adults,
        disabledMin: adults <= 1,
        disabledMax: guests >= 8,
      },
      {
        key: "children",
        title: "Children",
        subtitle: "(Max: 8 total guests/room) Age-based rates may be available",
        value: children.length,
        disabledMin: !children.length,
        disabledMax: guests >= 8,
      },
      ...children.map((age, i) => ({
        key: "child",
        index: i,
        title: `Child ${i + 1}: Age`,
        value: age,
        disabledMin: age <= 0,
        disabledMax: age >= 17,
      })),
    ],
    [rooms, roomsText, adults, children, guests],
  );

  const handleMinus = useCallback(
    (e) => {
      const { key, index } = e.target.dataset;

      if (key === "rooms") {
        let newValue = rooms - 1;
        newValue = newValue <= 1 ? 1 : newValue;
        setRooms(newValue);
        return;
      }

      if (key === "adults") {
        let newValue = adults - 1;
        newValue = newValue <= 1 ? 1 : newValue;
        setAdults(newValue);

        return;
      }

      if (key === "children") {
        const newValue = children.slice(0, -1);
        setChildren(newValue);
        return;
      }

      // Key: child
      const i = Number(index);
      const newValue = children[i] - 1;
      const newArray = [...children];
      newArray.splice(i, 1, newValue <= 0 ? 0 : newValue);
      setChildren(newArray);
    },
    [rooms, adults, children],
  );

  const handlePlus = useCallback(
    (e) => {
      const { key, index } = e.target.dataset;

      if (key === "rooms") {
        let newValue = rooms + 1;
        newValue = newValue >= 6 ? 6 : newValue;
        setRooms(newValue);
        return;
      }

      if (key === "adults") {
        let newValue = adults + 1;
        newValue = newValue >= 8 ? 8 : newValue;
        setAdults(newValue);
        return;
      }

      if (key === "children") {
        const newValue = [...children, 0];
        setChildren(newValue);
        return;
      }

      // Key: child
      const i = Number(index);
      const newValue = children[i] + 1;
      const newArray = [...children];
      newArray.splice(i, 1, newValue >= 17 ? 17 : newValue);
      setChildren(newArray);
    },
    [rooms, adults, children],
  );

  return (
    <div className={styles.guestPicker} data-testid="offers-guest-picker">
      {data.map(
        ({ key, index, title, subtitle, value, disabledMin, disabledMax }) => {
          // From 10-25 other rows are hidden
          if (key !== "rooms" && rooms > 4) {
            return;
          }

          return (
            <div key={key + (index ?? "")} className={styles.row}>
              <div className="titles">
                <p className={styles.title}>{title}</p>
                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </div>
              <div className={styles.actions}>
                <button
                  data-testid="guest-decrease"
                  aria-label={`Decrease ${title}`}
                  type="button"
                  className={classNames(styles.btn, styles.btnMinus)}
                  data-key={key}
                  data-index={index}
                  onClick={handleMinus}
                  disabled={disabledMin}
                >
                  −
                </button>
                <span className={styles.value}>
                  {key === "child" && !value ? "<1" : value}
                </span>
                <button
                  data-testid="guest-increase"
                  aria-label={`Increase ${title}`}
                  type="button"
                  className={classNames(styles.btn, styles.btnPlus)}
                  data-key={key}
                  data-index={index}
                  onClick={handlePlus}
                  disabled={disabledMax}
                >
                  +
                </button>
              </div>
            </div>
          );
        },
      )}
      {!isDefault && (
        <div className={styles.reset}>
          <button data-testid="guests-reset" type="reset" className={styles.resetBtn} onClick={reset}>
            Reset
          </button>
        </div>
      )}
    </div>
  );
};
